import {
  GridColumn as Column,
  GridCellProps,
} from '@progress/kendo-react-grid';
import {
  DateCell,
  DateLinkCell,
  FilterBarMainGrid,
  LinkCell,
  ReportsFilesDownloadIcon,
} from 'Components/Grid';
import { DatePickerFilterCell } from 'Components/Grid/FilterCells/DatePickerFilterCell';
import { DebouncedTextFilterCell } from 'Components/Grid/FilterCells/DebouncedTextFilterCell';
import { DropdownFilterCell } from 'Components/Grid/FilterCells/DropdownFilterCell';
import {
  FormProvider,
  GridProvider,
  useEnums,
  useGlobalContext,
} from 'Context';
import {
  ColumnSelectorProvider,
  useColumnSelectorState,
} from 'Context/ColumnSelectorProvider';
import {
  defaultGridProps,
  FilterBarFilterType,
  FilterConfig,
  FilterModelItem,
  IFilterConfig,
  StaticFilterConfig,
} from 'Models';
import { DefaultColumnsSelection } from 'Models/ColumnSelection';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import {
  AddEditSubRoute,
  AppointmentSubRoute,
  ClaimantsSubRoute,
  ClaimsSubRoute,
  ROUTES,
} from 'Navigation';
import React, { Fragment, HTMLProps, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  buildEndpoint,
  buildRoute,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATETIME24H_FORMAT,
  useDataFetcher,
  useGridSearch,
} from 'Utilities';
import { DropdownDbOption } from 'v3/Components/Form/Dropdown';
import NewReferralButton from 'v3/Components/Shared/NewReferralButton';
import ClientSelector from 'v3/Components/Shared/ClientSelector';
import DownloadIcon from '../../Assets/Icons/download.svg';
import {
  appointmentStatusCell,
  referralStatusCell,
} from 'v3/Components/Shared/StatusPill';
import { dueDateTimeCell } from 'Components/Grid/DisplayCells/CustomCells';
import { ColumnMenu } from 'Components/Grid/TableHeader/ColumnMenu';
import { DateRangePickerFilterCell } from 'Components/Grid/FilterCells/DateRangePickerFilterCell';
import { Render } from 'Components/Display';

class ReferralSearch {
  searchKey?: string;
  id?: string;
  claimId?: string;
  dueDate?: Date;
  referralDate?: Date;
}

export enum ReferralGridIds {
  DetailedSearch = 'detailedSearch',
}

export const AllClaimants: React.FC = () => {
  return (
    <ColumnSelectorProvider
      columns={DefaultColumnsSelection}
      columnSelectorName="allReferralsColumns"
    >
      <GridProvider entityName="Referral">
        <div className="main-grid-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h1>All Referrals</h1>
            <div className="grids-header-client-selector">
              <ClientSelector containerClassName="client-selector--large" />
            </div>
          </div>

          <NewReferralButton />
        </div>
        <AllClaimantsFilteredGrid />
      </GridProvider>
    </ColumnSelectorProvider>
  );
};

export type ReferralsFilteredGridProps =
  HTMLProps<ReferralsFilteredGridProps> & {
    id?: string;
  };

export const AllClaimantsFilteredGrid: React.FC<ReferralsFilteredGridProps> = ({
  id,
  ...props
}) => {
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const allClaimantsSearch = new ReferralSearch();
  allClaimantsSearch.claimId = id;
  return (
    <FormProvider formModel={allClaimantsSearch}>
      <AllClaimantsGrid id={id} />
    </FormProvider>
  );
};

const lineOfBusinessCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <td>{props.dataItem.lineOfBusiness?.displayName}</td>;
};
type FilterModel = {
  services?: DropdownDbOption[];
  submittedBy?: DropdownDbOption[];
  subServices?: DropdownDbOption[];
};
const AllClaimantsGrid: React.FC<ReferralsFilteredGridProps> = ({
  id,
  ...props
}) => {
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const isClient = userdata?.isClient ?? false;
  const isInternal = userdata?.isInternal ?? false;
  const isAccountsManager = userdata?.isAccountsManager ?? false;
  const isMedicalDirector = userdata?.isMedicalDirector ?? false;
  const columnSelectorState = useColumnSelectorState();
  const showDateFilters = userdata?.isInternal || userdata?.isAdmin;
  const history = useHistory();
  const [exportEndpoint, setExportEndpoint] = useState(
    buildEndpoint(
      ENDPOINTS.referrals.endpointString('ExportToCsv') +
      `${getSelectedColumnsQuery(columnSelectorState)}`
    )
  );

  useEffect(() => {
    setExportEndpoint(
      buildEndpoint(
        ENDPOINTS.referrals.endpointString('ExportToCsv') +
        `${getSelectedColumnsQuery(columnSelectorState)}`
      )
    );
  }, [columnSelectorState]);

  const filters: IFilterConfig[] = [
    new FilterConfig(
      'searchKey',
      FilterBarFilterType.SearchText,
      'Stradix Number, Claim Number, Claimant, Referral Number, Doctor'
    ),
    new FilterConfig('dueDate', FilterBarFilterType.DateRange, 'Due Date'),
    new FilterConfig(
      'referralDate',
      FilterBarFilterType.DateRange,
      'Referral Date'
    ),
    new StaticFilterConfig('claimId'),
    new StaticFilterConfig('clientId'),
  ];
  const [gridToggler] = useGridSearch();
  const [filtersEnabled, setFiltersEnabled] = useState(true);
  const dateColumnWidth = filtersEnabled ? '260px' : '200px';
  const { state, lineOfBusiness } = useEnums();
  const [filtersModel, filtersStatus] = useDataFetcher<FilterModel>(
    `${ENDPOINTS.referrals.base}/GetFiltersModel`,
    {},
    (d) => d.data,
    undefined,
    true,
    '3.0'
  );

  function navigateToReferral(dataItem: any) {
    history.push(
      buildRoute(ROUTES.REFERRAL, dataItem.referralId, ClaimsSubRoute.View)
    );
  }

  const referralNumberCell: React.FC<GridCellProps> = (
    props: GridCellProps
  ) => {
    function referralRoute(id: string) {
      return buildRoute(AddEditSubRoute.Referral, id, ROUTES.VIEW);
    }
    const endpoint = `${ENDPOINTS.appointmentreports.base}/ClosedReferral`;
    return (
      <LinkCell
        to={referralRoute(props.dataItem.id)}
        className="k-grid-content-sticky"
        style={{
          borderRight: '1px solid #e0e0e0 !important',
          left: 0,
          right: 0,
        }}
        bold
        linkField="referralId"
        subRoute={ClaimsSubRoute.View}
        {...props}
      >
        {!(isInternal || isAccountsManager || isMedicalDirector) &&
          props?.dataItem?.appointment?.appointmentStatus?.displayName ===
          'Closed' && (
            <ReportsFilesDownloadIcon
              fileControllerEndpoint={endpoint}
              display={<img src={DownloadIcon} alt="downloadIcon" />}
              {...props}
              id={props.dataItem.id}
            />
          )}
      </LinkCell>
    );
  };

  return (
    <React.Fragment>
      <FilterBarMainGrid
        gridId="AllReferrals"
        isMainGrid
        apiVersion="3.0"
        headerTitle="Referrals"
        filterBarProps={{
          filterConfigurations: filters,
          filterValues: {},
          isColumnSelectorEnabled: false,
        }}
        refreshToggle={gridToggler}
        readEndpoint={ENDPOINTS.referrals.read}
        exportEndpoint={`${ENDPOINTS.referrals.base}/exportToCSV`}
        exportEndpointVersion="3.0"
        enableGridRefresh={true}
        defaultFiltersEnabled={true}
        onFiltersEnabledChange={setFiltersEnabled}
        {...defaultGridProps}
        style={{ cursor: 'pointer' }}
        onRowClick={(e) => navigateToReferral(e.dataItem)}
      >
        {columnSelectorState.columns.find((x) => x.field === 'stradixNumber')
          ?.checked && (
            <Column
              field="stradixNumber"
              cell={referralNumberCell}
              title="Stradix Number"
              width="180px"
              locked={true}
              filterable
              filterCell={DebouncedTextFilterCell}
              columnMenu={ColumnMenu}
              className="column-stradix-number"
            />
          )}
        {columnSelectorState.columns.find((x) => x.field === 'claimantName')
          ?.checked && (
            <Column
              field="claimantName"
              width="180px"
              filterable
              filterCell={DebouncedTextFilterCell}
              title="Claimant Name"
              columnMenu={ColumnMenu}
            />
          )}
        {userdata?.isInternal && (
          <Column
            field="clientName"
            title="Client Name"
            filterCell={DebouncedTextFilterCell}
            width="200px"
            columnMenu={ColumnMenu}
          />
        )}
        {userdata?.isInternal &&
          (
            <Column
              field="reopenedDate"
              width={dateColumnWidth}
              filterable
              filterCell={(e) => <DateRangePickerFilterCell {...e} />}
              title="Re-Opened Date"
              cell={dateTimeFormatCell}
              columnMenu={ColumnMenu}
            />
          )}
        {columnSelectorState.columns.find((x) => x.field === 'referralDate')
          ?.checked && (
            <Column
              field="referralDate"
              width={dateColumnWidth}
              filterable
              filterCell={(e) => <DateRangePickerFilterCell {...e} />}
              title="Referral Date & Time"
              cell={dateTimeFormatCell}
              columnMenu={ColumnMenu}
            />
          )}
        <Column
          field="claimNumber"
          title="Claim Number"
          filterable
          filterCell={DebouncedTextFilterCell}
          width="188px"
          columnMenu={ColumnMenu}
        />
        <Column
          field="service"
          title="Service"
          width="140px"
          filterable
          filterCell={(x) =>
            DropdownFilterCell(
              x,
              undefined,
              filtersStatus === 'success' ? filtersModel.services : []
            )
          }
          columnMenu={ColumnMenu}
        />
        <Column
          field="subService"
          title="Sub Service"
          width="140px"
          filterable
          filterCell={(x) =>
            DropdownFilterCell(
              x,
              undefined,
              filtersStatus === 'success' ? filtersModel.subServices : []
            )
          }
          columnMenu={ColumnMenu}
        />
        {columnSelectorState.columns.find((x) => x.field === 'benefitState')
          ?.checked && (
            <Column
              field="benefitState"
              title="State of Jurisdiction"
              width="140px"
              filterable
              filterCell={(x) => DropdownFilterCell(x, state)}
              columnMenu={ColumnMenu}
            />
          )}
        {columnSelectorState.columns.find((x) => x.field === 'status')
          ?.checked &&
          !isClient && (
            <Column
              field="appointmentStatus"
              title="Status"
              width="200px"
              cell={(e) =>
                appointmentStatusCell({
                  ...e,
                  onClick: () => navigateToReferral(e.dataItem),
                })
              }
              filterCell={DebouncedTextFilterCell}
              columnMenu={ColumnMenu}
            />
          )}
        <Column
          field="referralSourceName"
          title="Submitted By"
          width="168px"
          filterable
          filterCell={(x) =>
            DropdownFilterCell(
              x,
              undefined,
              filtersStatus === 'success' ? filtersModel.submittedBy : []
            )
          }
          columnMenu={ColumnMenu}
        />
        <Column
          field="lineOfBusinessName"
          title="Line Of Business"
          filterCell={(x) => DropdownFilterCell(x, lineOfBusiness)}
          cell={lineOfBusinessCell}
          width="180px"
          columnMenu={ColumnMenu}
        />
        {!isClient && (
          <Column
            field="ownerName"
            title="Owner"
            width="160px"
            columnMenu={ColumnMenu}
            filterCell={DebouncedTextFilterCell}
          />
        )}
        <Column width={0}></Column>
      </FilterBarMainGrid>
    </React.Fragment>
  );
};

// deconstruct so that I can use the dataItem property alone
const linkDateColumn: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <DateLinkCell
      endpoint={ROUTES.APPOINTMENT}
      linkField="appointmentId"
      subRoute={AppointmentSubRoute.View}
      dateFormat={DEFAULT_DATE_FORMAT}
      {...props}
    />
  );
};

const dateFormatCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <DateCell {...props} dateFormat={DEFAULT_DATE_FORMAT} />;
};

const dateTimeFormatCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <DateCell {...props} dateFormat={DEFAULT_DATETIME24H_FORMAT} />;
};

const claimNumberCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <LinkCell
      endpoint={ROUTES.CLAIM}
      linkField="claimId"
      subRoute={ClaimsSubRoute.View}
      {...props}
    ></LinkCell>
  );
};

const claimantCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <LinkCell
      endpoint={ROUTES.CLAIMANT}
      linkField="claimantId"
      subRoute={ClaimantsSubRoute.View}
      {...props}
    ></LinkCell>
  );
};

const getSelectedColumnsQuery = (columnSelectorState: any) => {
  const selectedColumns = columnSelectorState.columns
    .filter((x: any) => x.checked)
    .map((x: any) => x.field);
  return '?' + selectedColumns.map((x: any) => `columns=${x}`).join('&');
};
